/**
 * MathGraph32 Javascript : Software for animating online dynamic mathematics figures
 * http://www.mathgraph32.org/
 * @Author Yves Biton (yves.biton@sesamath.net)
 * @License: GNU AGPLv3 https://www.gnu.org/licenses/agpl-3.0.html
 */
  // Version 6.0 : ON ajoute un angle d'affichage
const Color = require('../types/Color')
const Fonte = require('../types/Fonte')
const Pointeur = require('../types/Pointeur')
const Rect = require('../types/Rect')
const StyleEncadrement = require('../types/StyleEncadrement')
const kernel = require('../kernel/kernel')
const CElementGraphique = require('./CElementGraphique')
const Vect = require('../types/Vect')
const CValeurAngle = require('./CValeurAngle')

module.exports = CAffLiePt

/**
 * Classe ancêtre de tous les éléments affichant quelque chose sur la figure.
 * Version spéciale mtgApp car on doit pouvoir capturer un affichage même non encadré
 * @constructor
 * @extends CElementGraphique
 * @param {CListeObjets} listeProprietaire : La liste propriétaire.
 * @param {CImplementationProto} impProto : null ou la construction propriétaire.
 * @param {Boolean} estElementFinal : true si l'objet est un élément final de construction.
 * @param {Color} couleur : La couleur d'éciture de l'éditeur (et du cadre éventuel).
 * @param {Float} x_nom : L'abscisse d'affichage de l'éditeur
 * @param {Float} y_nom : L'ordonnée d'affichage de l'éditeur
 * @param {Integer} dec_x : Décalage horizontal du nom
 * @param {Integer} dec_y : Décalage vertical du nom
 * @param {Boolean} masque : true si l'éditeur est masqué
 * @param {CPt} pointLie : null ou pointe sur un point auquel l'affichage est lié.
 * @param {Integer} taillePolice : Indice de la taiile de police utilisée
 * @param {Boolean} encadrement : true si encadré.
 * @param {Boolean} effacementFond : true si on efface le fond de l'en-tête.
 * @param {Color} couleurFond : La couleur de fond de l'en-tête.
 * @param {Integer} alignementHorizontal : 0 pour alignement gauche, 1 pour centre, 2 pour droite.
 * @param {Integer} alignementVertical : 0 pour alignement vers le haut, 1 pour centré, 2 pour bas.
 * @param {CValeurAngle} angText : L'angle du texte par rapport à l'horizontale dans l'unité d'angle active
 * @returns {CAffLiePt}
 */
function CAffLiePt (listeProprietaire, impProto, estElementFinal, couleur,
    x_nom, y_nom, dec_x, dec_y, masque, pointLie, taillePolice, encadrement, effacementFond,
    couleurFond, alignementHorizontal, alignementVertical, angText) {
  if (arguments.length !== 0) {
    if (arguments.length === 1) CElementGraphique.call(this, listeProprietaire);
    else {
      // A revoir car les deux derniers éléments qui sont enregistrés dans le flux sont inutiles
      CElementGraphique.call(this, listeProprietaire, impProto, estElementFinal, couleur, false,
        dec_x, dec_y, masque, "", 0);
      this.x_nom = x_nom;
      this.y_nom = y_nom;
      this.pointLie = pointLie;
      this.taillePolice = taillePolice;
      this.encadrement = encadrement;
      this.effacementFond = effacementFond;
      this.couleurFond = couleurFond;
      this.alignementHorizontal = alignementHorizontal;
      this.alignementVertical = alignementVertical;
      if (angText) this.angText = angText; else
        this.angText = new CValeurAngle(listeProprietaire, 0);
    }
  }
  // Pour les LaTeX utilisés dans un CEditeurFormule, pointera sur le CEditeurFormule propriétaire
  this.owner = null;
  this.rectAff = new Rect(0,0,0,0);
  this.chaineLatex = "";
  this.div = null; // Nécessaire pour les CLatex

}
CAffLiePt.prototype = new CElementGraphique();
CAffLiePt.prototype.constructor = CAffLiePt;
CAffLiePt.prototype.superClass = "CElementGraphique";
CAffLiePt.prototype.className = "CAffLiePt";

CAffLiePt.alignHorLeft = 0;
CAffLiePt.alignHorCent = 1;
CAffLiePt.alignHorRight = 2;
CAffLiePt.alignVerTop = 0;
CAffLiePt.alignVerCent = 1;
CAffLiePt.alignVerLow = 2;

CAffLiePt.prototype.getStyleLatex = function() {
  return "top:0px;left:0px;position:absolute;font-family: \"Times New Roman\", Times, serif;font-size:" + Fonte.tailleLatex(this.taillePolice)
    +"px;visibility:hidden;"
}

CAffLiePt.prototype.setClone = function(ptel) {
  CElementGraphique.prototype.setClone.call(this, ptel);
  this.chaineAAfficher = ptel.chaineAAfficher;
  this.taillePolice = ptel.taillePolice;
  this.encadrement = ptel.encadrement;
  this.effacementFond = ptel.effacementFond;
  this.couleurFond = ptel.couleurFond;
  this.angText.valeur = ptel.angText.valeur; // Ajout version 6.0
  // this.rectAff.setRec(ptel.rectAff); // Ne pas mettre
};

/**
 * Ajout version 5.2 (numéro de version 16). Renverra true si l'objet possède deux éléments dec_x et dec_y doit être enregistré dans le flux
 * Sera redéfini à true dans CAffLiePt
 * @returns
 */
CAffLiePt.prototype.hasDec = function() {
  return true;
}

CAffLiePt.prototype.ajouteAntecedents = function(liste) {
  if (this.pointLie !== null) liste.add(this.pointLie);
};

CAffLiePt.prototype.depDe = function(p) {
  var dep = CElementGraphique.prototype.depDe.call(this, p) || this.angText.depDe(p);
  if (this.pointLie === null)
    return dep;
  else
    return dep || this.pointLie.depDe(p) ;
};

CAffLiePt.prototype.dependDePourBoucle = function(p) {
  var dep = (p === this) || this.angText.dependDePourBoucle(p);
  if (this.pointLie === null) return dep;
  else return dep || this.pointLie.dependDePourBoucle(p) ;
};

CAffLiePt.prototype.dependDePourCapture = function(p) {
  return this.depDe(p);
};

/**
 * Cette fonction est appelée par CImage car positionne fait des choses pas nécessaires pour ce type d'objet
 * @param infoRandom
 * @param dimf
 */
CAffLiePt.prototype.positionnePourIm = function(infoRandom, dimf) {
  this.angText.positionne();
  this.existe = this.angText.existe;
  if (this.pointLie !== null) this.existe = this.existe && this.pointLie.existe;

  if (!this.existe) return;
  if (this.pointLie !== null) {
    /* Modifié version mtgApp à cause des lieux d'objets
    if (this.pointLie.horsEcran) {
      this.existe = false;
      return;
    }
    */
    this.x_nom = this.pointLie.x; //
    this.y_nom = this.pointLie.y; //

    if (!dimf.dansFenetre(this.x_nom, this.y_nom)) {
      this.existe = false;
    }
  }
}

CAffLiePt.prototype.positionne = function(infoRandom, dimf) {
  this.positionnePourIm(infoRandom, dimf)
  this.chaineAAfficher = this.rendChaineAffichage();
  var len=this.chaineAAfficher.length;
  if ((this.chaineAAfficher.charAt(0) === "$") && (this.chaineAAfficher.charAt(len-1) === "$")) {
    var ch = this.chaineAAfficher.substring(1, len - 1);
    this.isToBeUpdated = (this.chaineLatex !== ch);
    this.chaineLatex = ch;
  }
};

/**
 * Fonction appelée soit ppour un CLaTeX soit pour un commentaire dont le texte commence et finit pat $
 * et qui détruit this.div quand il est non nul qui est la div provisoirement créé pour être traité parr MathJax
 */
CAffLiePt.prototype.deleteDiv = function() {
  if (this.div !== null) {
    document.body.removeChild(this.div);
    this.div = null;
  }
}


CAffLiePt.prototype.distancePoint = function(xp, yp, masquage) {
  if (!this.existe || (masquage && this.masque)) return -1;
  else {
    var rectAff = this.rectAff;
    var ang = this.angText.rendValeurRadian();
    if (ang === 0) {
      if (rectAff.contains(xp - this.x_nom - this.dec_x, yp - this.y_nom - this.dec_y)) return 0;
      else return -1;
    }
    else {
      var x = xp - this.x_nom - this.dec_x;
      var y = yp - this.y_nom - this.dec_y;
      var v = new Vect(0, 0, x, y);
      var w = new Vect();
      v.tourne(-ang, w);
      var x1 = w.x;
      var y1 = w.y;
      if (rectAff.contains(x1, y1)) return 0;
      else return -1;
    }
  }
};

CAffLiePt.prototype.remplacePoint = function(ancienPoint, nouveauPoint) {
  if (this.pointLie === ancienPoint) this.pointLie = nouveauPoint;
};

/**
 * Fonction préparant l'affichage par MathJax en créant un div provisoire
 * @param bMemeMasque : Si true la prépartion de fait même si l'affichage est caché (sert dans
 * la boîte de dialogue de protocole)
 */
CAffLiePt.prototype.setReady4MathJax = function(bMemeMasque) {
  const CLatex = require('./CLatex')
  var len;
  if (this.chaineAAfficher.charAt(0) === "$") {
    len = this.chaineAAfficher.length;
    if (this.chaineAAfficher.charAt(len-1) === "$")
      CLatex.prototype.setReady4MathJax.call(this, bMemeMasque);
  }
};

CAffLiePt.prototype.setReady4MathJaxUpdate = function() {
  const CLatex = require('./CLatex')
  var len;
  if (this.chaineAAfficher.charAt(0) === "$") {
    len = this.chaineAAfficher.length;
    if (this.chaineAAfficher.charAt(len-1) === "$") {
      CLatex.prototype.setReady4MathJaxUpdate.call(this);
    }
  }
};

/**
 * Ajout version 6.0
 * Positionne l'angle du g element
 */
/*
CAffLiePt.prototype.positionneAngText = function(g) {
  // Ajout version Version 6.0 //////////////////////////////////////////
  var ang = -this.angText.rendValeurRadian();
  var ra = this.rectAff;
  var x = ra.x + ra.width/2;
  var y = ra.y + ra.height/2;
  if (ang !== 0) {
    var angs = String(ang*kernel.ConvRadDeg);
    var u = new Vect(0, 0, this.x_nom + this.dec_x, this.y_nom + this.dec_y);
    var v = new Vect();
    u.tourne(ang, v);
    var x1 = String(-x + v.x);
    var y1 = String(-y + v.y);

    g.setAttribute("transform", "translate(" + String(x) + "," + String(y) + ") rotate(" + angs + ") translate(" + x1 + "," + y1 + ")");
  }
  else g.setAttribute("transform", "translate(" + String(this.x_nom + this.dec_x) + "," + String(this.y_nom + this.dec_y) + ")");
  /////////////////////////////////////////////////////////////////////////

}
*/
CAffLiePt.prototype.positionneAngText = function(g) {
  // Ajout version Version 6.0 //////////////////////////////////////////
  var ang = -this.angText.rendValeurRadian();
  var x = this.x_nom + this.dec_x;
  var y = this.y_nom + this.dec_y;
  if (ang !== 0) {
    var angs = String(ang*kernel.ConvRadDeg);
    var u = new Vect(0, 0, x, y);
    var v = new Vect();
    u.tourne(ang, v);
    var x1 = String(v.x);
    var y1 = String(v.y);
    g.setAttribute("transform", "rotate(" + angs + ") translate(" + x1 + "," + y1 + ")");
  }
  else g.setAttribute("transform", "translate(" + String(this.x_nom + this.dec_x) + "," + String(this.y_nom + this.dec_y) + ")");

}

CAffLiePt.prototype.createg = function(svg) {
  const CLatex = require('./CLatex')
  var g,t,style,ind,ch,haut,h,anchor,len,nblig,y,ligaf,st,styleexp,ch2,v,sty;
  len = this.chaineAAfficher.length;
  // Ajout version mtgApp
  if (len ===0) return kernel.cens("g");
  //
  // Supprimé version 6.0
  /*
  this.oldx_nom = this.x_nom;
  this.oldy_nom = this.y_nom;
  this.olddec_x = this.dec_x;
  this.olddec_y = this.dec_y;
  */
  if (this.chaineAAfficher.charAt(0) === "$") {
    if (this.chaineAAfficher.charAt(len-1) === "$") {
      // this.chaineLatex = this.chaineAAfficher.substring(1, len - 1);
      // this.style = "top:0px;left:0px;position:absolute;alignment-adjust:baseline;font-size: " +
     //    Fonte.taille(this.taillePolice)+"px;visibility: hidden;";
      return CLatex.prototype.createg.call(this);
    }
  }
  this.chaineAffichee = this.chaineAAfficher;
  // haut = Fonte.taille(this.taillePolice); Modifié version 5.0
  haut = this.taillePolice;
  g = kernel.cens("g");
  // L'élément va être d'abord caché. S'il est encadré ou efface le fond on rajoutera un élément en tête
  switch (this.alignementHorizontal) {
    case CAffLiePt.alignHorLeft :
      anchor = "start";
      break;
    case CAffLiePt.alignHorCent :
      anchor = "middle";
      break;
    case CAffLiePt.alignHorRight :
      anchor = "End";
  }
  // On compte le nombre de lignes
  nblig = 1;
  ind = -1;
  while ((ind=this.chaineAAfficher.indexOf("\n",ind+1)) !== -1) nblig++;
  h = (nblig === 1) ? haut : haut + (nblig-1)*(haut+2);
  // y = this.dec_y; // Corrigé version 6.0.1
  y = 0;
  if (this.chaineAAfficher.search(/#H|#L/) !== -1) {
    v = haut/3;
    h += v;
    y += v;
  }
  switch (this.alignementVertical) {
  case CAffLiePt.alignVerCent :
    y -= h/2 + 1;
    break;
  case CAffLiePt.alignVerLow :
    y -= h + 2;
  }
  // Modifié version 4.9.2.3
  // ligaf = y + haut - 1;
  ligaf = y + haut - 2;
  // this.rectAff.height = h;
  // Modifié version 4.9.9.4
  // style = "text-anchor:" + anchor + ";font-size:" + haut +"px;" + "fill:" + this.couleur.rgb()+";";
  // Modifié version 5.1.4 : IL faur préciser une fonte pour les textes
  sty = "font-family:'Times New Roman',Times,serif;text-anchor:" + anchor + ";fill:" + this.color+";"
  style = sty + "font-size:" + haut +"px;";
  // Ajout version 5.0
  styleexp = sty + ";font-size:" + String(Math.round(haut*2/3)) +"px;";
  //
  // Modification version 5.0 : Une variable globale mémorisant le style d'écriture : italique, gras, souligne ou une combinaison de ces styles
  st = new Pointeur(Fonte.Normal);
  if (this.chaineAAfficher.indexOf("\n") === -1) {
    t = CAffLiePt.createLine(this.chaineAAfficher, haut, style, styleexp, ligaf, st);
    g.appendChild(t);
  }
  else {
    ch = this.chaineAAfficher;
    // h = haut;
    while ((ind=ch.indexOf("\n")) !== -1) {
      ch2 = ch.substring(0, ind);
      t = CAffLiePt.createLine(ch2, haut, style, styleexp, ligaf,st);
      g.appendChild(t);
      // Modification version 5.0 pour traiter exposants et indices
      ligaf += haut + ((ch2.search(/#H|#L/) !== -1) ? haut/3 + 2 : 2);
      ch = ch.substring(ind+1);
    }
    t = CAffLiePt.createLine(ch, haut, style, styleexp, ligaf, st);
    g.appendChild(t);
  }
  // this.rectAff.x = this.x_nom + this.dec_x - 1;
  // this.rectAff.y = y - 1;
  /* Modification version mtgApp
  if (this.effacementFond || (this.encadrement !== StyleEncadrement.Sans)) {
    // Comme on ne peut pas connaître des dimensions sur le svg de la chaîne, dans le cas où elle est encadrée ou efface
    // le fond, il faut l'afficher une première fois pour connaître ses dimensions
    g.setAttribute("visibility", "hidden");
    svg.appendChild(g);
    // this.rectAff.height = h + 7;
    this.rectAff.height = h + 6;
    this.rectAff.width = g.getBBox().width + 4;
    // this.rectAff.x  = g.getBBox().x - 2;
    this.rectAff.x  = g.getBBox().x;
    // this.rectAff.y  = y - 2;
    this.rectAff.y  = y;
    svg.removeChild(g);
    g.setAttribute("visibility", "visible");
    this.creeRectangle(g);
  }
  */
  g.setAttribute("visibility", "hidden");
  var svgAux = document.getElementById("mtgSvgAux");
  svgAux.appendChild(g);
  var box = g.getBBox();
  this.rectAff.x = box.x;
  this.rectAff.y = y;
  /*
  ////////////////////////////////////////////////////////////////////////////
  // Ajout version 6.0 : Quand l'affichage doit être translaté et n'a pas à //
  // être réaffiché; il faut connaître la position relative entre le        //
  // rectangle d'affichage et (x_nom, y_no);
  */
  // this.posRel = {x: this.rectAff.x - this.x_nom, y : this.rectAff.y - this.y_nom};
  ////////////////////////////////////////////////////////////////////////////
  if (this.effacementFond || (this.encadrement !== StyleEncadrement.Sans)) {
    this.rectAff.width = box.width + 4;
    this.rectAff.height = h + 6;
    svgAux.removeChild(g);
    g.setAttribute("visibility", "visible");
    this.creeRectangle(g);
  }
  else {
    this.rectAff.width = box.width;
    this.rectAff.height = h;
    svgAux.removeChild(g);
    g.setAttribute("visibility", "visible");
  }
  //

  g.setAttribute("pointer-events", "none");
  // Ajout version Version 6.0 //////////////////////////////////////////
  this.positionneAngText(g);
  /////////////////////////////////////////////////////////////////////////
  return g;
};
/**
 * Fonction créant une ligne de texte dans le cas d'un affichage de plusieurs lignes.
 * Renvoie un élément du type text de svg.
 * @param {String} ch : Le texte de la ligne.
 * @paam {Integer} taille : La taille de la police utilisée
 * @param {String} style : le style d'affichage.
 * @param {Number} x : L'abscisse de début d'affichage.
 * @param {Number} y : L'ordonnée de début d'affichage.
 * @param {Pointeur} st : Contient le style d'écriture actif (combinaison entre normal, italique, gras et souligné)
 * @returns {svg.text}
 */
CAffLiePt.createLine = function(ch, taille, style, styleexp, y, st) {
  var cont,i,tspan,s,car,ind,diese,affspecial,dy,indpf;
  var styleEc = st.getValue();
  var t = kernel.cens("text",{
    x : 0,
    y : y,
    style : style
  });
  s = ch.split(/(#)/); // Parenthèses pour que le # soient dans le tableau
  affspecial = false;
  diese = false;
  dy = 0;
  for (i=0; i < s.length; i++) {
    if (s[i] === "#") {
      if (!diese) {
        affspecial = true;
        diese = true;
      }
      else {
        tspan = kernel.cens("tspan",{
          style : style + Fonte.styleEcriture(styleEc)
        });
        cont = document.createTextNode("#");
        tspan.appendChild(cont);
        t.appendChild(tspan);
        diese = false;
      }
    }
    else {
      if (s[i].length !== 0) {
        diese = false;
        car = s[i].charAt(0);
        ind = 1;
        if (affspecial) {
          if ((car === "I") || (car === "N") || (car === "G") || (car === "U")) {
            if (car === "I") {
              styleEc |= Fonte.Italique;
              ind = 1;
            }
            else if (car === "N") {
              styleEc = Fonte.Normal;
              ind = 1;
            }
            else if (car === "G") {
              styleEc |= Fonte.Gras;
              ind = 1;
            }
            else if (car === "U") {
              styleEc |= Fonte.Underline;
              ind = 1;
            }
            if (ind !== s[i].length) {
              tspan = kernel.cens("tspan", {
                style : style + Fonte.styleEcriture(styleEc),
                dy : dy+"px"

            });
              cont = document.createTextNode(s[i].substring(ind));
              tspan.appendChild(cont);
              t.appendChild(tspan);
              affspecial = false;
              dy = 0;
              // if (dy != 0) dy = -dy; // Un seul niveau d'exposant ou d'indice
            }
          }
          else {
            if (affspecial && ((car === "H") || (car === "L"))) {
              if (s[i].charAt(1) !== "(") {
                ind = 0;
                indpf = s[i].length;
              }
              else {
                ind = 2;
                indpf = s[i].indexOf(")");
                if (indpf ===  -1) indpf = s[i].length;
                if (car === "H") dy = - taille/3;
                else dy = taille/3;
              }
              /* Modifié version mtgApp
              tspan = document.createElementNS(kernel.svgns,"tspan");
              tspan.setAttribute("style", styleexp+Fonte.styleEcriture(styleEc));
              tspan.setAttribute("dy",dy+"px");
              */
              tspan = kernel.cens("tspan",{
               style : styleexp+Fonte.styleEcriture(styleEc),
                dy : dy+"px"
              });

              cont = document.createTextNode(s[i].substring(ind, indpf));
              tspan.appendChild(cont);
              t.appendChild(tspan);
              affspecial = false;
              dy = -dy; // Un seul niveau d'exposant ou d'indice
              if (indpf < s[i].length - 1) { // Si il reste de caractères après la parenthèse fermante, affichage normal
                 tspan = kernel.cens("tspan",{
                  style : style+Fonte.styleEcriture(styleEc),
                  dy : dy+"px"
                });
                cont = document.createTextNode(s[i].substring(indpf +1));
                tspan.appendChild(cont);
                t.appendChild(tspan);
                affspecial = false;
                dy = 0;
              }
            }
            else {
              tspan = kernel.cens("tspan",{
                style : style+ Fonte.styleEcriture(styleEc),
                dy : dy+"px"
              });
              cont = document.createTextNode(s[i]);
              tspan.appendChild(cont);
              t.appendChild(tspan);
              affspecial = false;
              dy = 0;
            }
          }
        }
        else {
          tspan = kernel.cens("tspan",{
           style : style+ Fonte.styleEcriture(styleEc),
            dy : dy+"px"
          });
          cont = document.createTextNode(s[i]);
          tspan.appendChild(cont);
          t.appendChild(tspan);
          affspecial = false;
          dy = 0;
        }
      }
      else affspecial = false;
    }
  }
  st.setValue(styleEc);
  t.setAttribute("pointer-events", "none");
  return t;
};

CAffLiePt.prototype.trace = function(svg){
  // Attention : paramètre svg nécessaire
  var g = this.createg(svg);
  g.setAttribute("id",this.id);
  // g.setAttribute("visibility", "hidden");
  svg.appendChild(g);
  this.g = g;
};
/**
 * Dans le cas où l'affichage est encadré, crée un rect de svg qui sera inéséré
 * dans le g element représentant l'objet juste avant l'affichage.
 * @param {svg.rect} g
 * @returns {undefined}
 */
CAffLiePt.prototype.creeRectangle = function(g) {
  // var style = "shape-rendering:crispEdges;stroke-width:1px;";
  var style = "stroke-width:0.5px;";
  // Modifié version 4.9.9.4
  // style += (this.encadrement === StyleEncadrement.Sans) ? "stroke:none;" : "stroke:" + this.couleur.rgb()+";";
  style += (this.encadrement === StyleEncadrement.Sans) ? "stroke:none;" : "stroke:" + this.color + ";";
  if (this.effacementFond) style += "fill:" + this.couleurFond.rgb()+";";
  else style += "fill:none;";
  var r = kernel.cens("rect",{
    style : style,
    x : this.rectAff.x - 2,
    y : this.rectAff.y - 2,
    width : this.rectAff.width + 3,
    height : this.rectAff.height,
    "pointer-events" : "none"
  });
  g.insertBefore(r, g.childNodes[0]);
  if (this.encadrement === StyleEncadrement.Effet3D) { // Ajout d'un effet 3D
    // var pol = document.createElementNS(kernel.svgns,"polygon"); // Modifié version mtgApp
    style = "stroke-width:1px;stroke:" + this.color+";fill:"+ this.color+";";
    /* Modifié version mtgApp
    pol.setAttribute("style", style);
    */
    var x = this.rectAff.x - 2;
    var y = this.rectAff.y - 2;
    var w = this.rectAff.width + 3; // Modifié version 4.9.9.4
    var h = this.rectAff.height;
    var s1 = String(x+w);
    var s2 = String(y+h);
    var s3 = String(x+w+1);
    var s4 = String(y+h+1);
    var pol = kernel.cens("polygon",{
      style : style,
      points : x + "," + s2 + " " + s1 + "," +
      s2 + " " + s1 + "," + y + " " + s3 + "," + String(y+1) + " " +
      s3 + "," + s4 + " " + String(x+1) + "," + s4
    });
    g.insertBefore(pol, g.childNodes[1]);
  }
};
/**
 * Fonction retirant de g le rectangle encadrant l'affichage lorsque celui-ci est présent
 * et le remplaçant par un nouveau.
 * @param {type} g
 * @returns {undefined}
 */
CAffLiePt.prototype.remplaceRectangle = function(g) {
  g.removeChild(g.childNodes[0]);
  if (this.encadrement === StyleEncadrement.Effet3D) g.removeChild(g.childNodes[0]);
  this.creeRectangle(g);
};

// Modifié version 5.0 : taille représente maintenant la taille réelle
CAffLiePt.prototype.update = function(svg) {
  const CLatex = require('./CLatex')
  var g,len;
  if (this.chaineAAfficher.charAt(0) === "$") {
    len = this.chaineAAfficher.length;
    if (this.chaineAAfficher.charAt(len-1) === "$") {
      CLatex.prototype.update.call(this, svg);
    }
  }
  else {
    /*
    decalagex = this.x_nom - this.oldx_nom + this.dec_x- this.olddec_x;
    decalagey = this.y_nom - this.oldy_nom + this.dec_y - this.olddec_y;
    this.olddec_x = this.dec_x;
    this.olddec_y = this.dec_y;
    this.oldx_nom = this.x_nom;
    this.oldy_nom = this.y_nom;
    */
    var oldg = this.g;
    // if (this.chaineAffichage === this.chaineAffichee) {
    if (this.chaineAAfficher === this.chaineAffichee) {
      // Inutile de recréer le composant on le translate simplement
      //////////////////////////////////////////////////////////////////////////
      // Ajout version 6.0 /////////////////////////////////////////////////////
      this.positionneAngText(oldg);
      //////////////////////////////////////////////////////////////////////////
      if (this.effacementFond || (this.encadrement !== StyleEncadrement.Sans)) {
        this.remplaceRectangle(oldg);
      }
    }
    else {
      g = this.createg(svg); // Paramètre nécessaire pour le CLatex
      svg.replaceChild(g, oldg);
      g.setAttribute("id",this.id);
      this.g = g;
    }
  }
  // Ajout version 5.0.1
  if ((this.pointLie !== null) && !this.pointLie.dansFenetre)
    this.g.setAttribute("visibility", "hidden");
  else this.g.setAttribute("visibility", "visible");
};

CAffLiePt.prototype.reCreateDisplay = function(svg) {
  var oldg = this.g;
  var g = this.createg(svg); // Paramètre nécessaire pour le CLatex
  svg.replaceChild(g, oldg);
  g.setAttribute("id",this.id);
  this.g = g;
}

// Spécial JavaScript
/**
 * Fonction renvoyant le code à rajouter au début du code LaTeX pour qu'un affichage LaTeX
 * ait la couleur désirée.
 * @returns {String}
 */
CAffLiePt.prototype.prelatex = function() {
  // Modifié version 4.9.9.4
  /*
  return "\\color[RGB]{"+Math.floor(this.couleur.red) +"," + Math.floor(this.couleur.green) + "," +
    Math.floor(this.couleur.blue) + "}";
  */
 return "\\color{"+this.color+"}";
};

CAffLiePt.prototype.read = function(inps, list){
  CElementGraphique.prototype.read.call(this,inps, list);
  var ind1 = inps.readInt();
  if (ind1 === -1) this.pointLie = null;
  else this.pointLie = list.get(ind1,"CPt");
  this.taillePolice = inps.readByte();
  var numeroVersion = list.numeroVersion;
  // Modification version 5.0 : taillePolice est maintenant la taille réelle de la police
  if (numeroVersion < 15) this.taillePolice = Fonte.taille(this.taillePolice);
  if ((this.listeProprietaire.numeroVersion < 11)) {
    this.x_nom = inps.readDouble();
    this.y_nom = inps.readDouble();
  }
  else {
    if (this.pointLie === null) {
      this.x_nom = inps.readDouble();
      this.y_nom = inps.readDouble();
    }
  }
  this.encadrement = inps.readByte();
  this.effacementFond = inps.readBoolean();
  // Modification pour le passage en version RVB à partir de la version 2.5 (n°5)
  if (this.effacementFond) {
    var r = inps.readByte();
    var g = inps.readByte();
    var b = inps.readByte();
    this.couleurFond = new Color(r,g,b);
  }
  else this.couleurFond = Color.white; // Ne sert pas
  var tv = this.listeProprietaire.numeroVersion >= 9;
  this.alignementHorizontal =  tv ? inps.readInt(): CAffLiePt.alignHorLeft;
  this.alignementVertical = tv ? inps.readInt() : CAffLiePt.alignVerTop;

  /*
  this.styleLatex = "top:0px;left:0px;position:absolute;font-size:" + Fonte.tailleLatex(this.taillePolice)
        +"px;visibility:hidden;";
  */
  this.chaineLatex = ""; // Ajout version 4.9.2
  if (numeroVersion >= 18) {
    this.angText = new CValeurAngle();
    this.angText.read(inps, list);
  } // Ajout version 6.0
  else this.angText = new CValeurAngle(list, 0);
};

CAffLiePt.prototype.write = function(oups, list){
  CElementGraphique.prototype.write.call(this,oups, list);
  var ind1;
  if (this.pointLie === null) ind1 = -1;
  else ind1 = list.indexOf(this.pointLie);
  oups.writeInt(ind1);
  oups.writeByte(this.taillePolice);
  if (this.pointLie === null) {
    oups.writeDouble(this.x_nom);
    oups.writeDouble(this.y_nom);
  }
  oups.writeByte(this.encadrement);
  oups.writeBoolean(this.effacementFond);
  if (this.effacementFond) {
    oups.writeByte(this.couleurFond.getRed());
    oups.writeByte(this.couleurFond.getGreen());
    oups.writeByte(this.couleurFond.getBlue());
  }
  oups.writeInt(this.alignementHorizontal);
  oups.writeInt(this.alignementVertical);
  this.angText.write(oups, list); // Ajout version 6.0
};
